import { TFunction } from 'i18next';
import type { DriveStep } from 'driver.js';

const sel = (id: string) => `[data-tour="admin-participants-${id}"]`;

function clickTourElement(id: string) {
  document.querySelector<HTMLElement>(sel(id))?.click();
}

function dismissPopover(panelId: string) {
  // Find the close button inside the tour panel and click it
  const panel = document.querySelector<HTMLElement>(sel(panelId));
  const closeBtn = panel?.querySelector<HTMLElement>('button[aria-label]');
  if (closeBtn) {
    closeBtn.click();
    return;
  }
  // Fallback: click outside by dispatching on the backdrop
  const backdrop = document.querySelector<HTMLElement>('.MuiPopover-root');
  backdrop?.click();
}

function dismissMenu() {
  const backdrop = document.querySelector<HTMLElement>('.MuiModal-backdrop');
  if (backdrop) {
    backdrop.click();
    return;
  }
  document.body.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true, cancelable: true }),
  );
}

function liftDialogForTour(retries = 20) {
  const root = document.querySelector<HTMLElement>('.MuiDialog-root');
  if (!root) {
    if (retries > 0) {
      setTimeout(() => liftDialogForTour(retries - 1), 50);
    }
    return;
  }
  root.style.zIndex = '100002';
  const backdrop = root.querySelector<HTMLElement>('.MuiBackdrop-root');
  if (backdrop) {
    backdrop.style.opacity = '0';
    backdrop.style.pointerEvents = 'none';
  }
}

function restoreAndCloseDialog(closeFn?: () => void) {
  const root = document.querySelector<HTMLElement>('.MuiDialog-root');
  if (root) {
    root.style.zIndex = '';
    const backdrop = root.querySelector<HTMLElement>('.MuiBackdrop-root');
    if (backdrop) {
      backdrop.style.opacity = '';
      backdrop.style.pointerEvents = '';
    }
  }
  closeFn?.();
}

function liftMenuForTour(retries = 10) {
  const root = document.querySelector<HTMLElement>('.MuiPopover-root');
  if (!root) {
    if (retries > 0) {
      setTimeout(() => liftMenuForTour(retries - 1), 50);
    }
    return;
  }
  root.style.zIndex = '100002';
  const backdrop = root.querySelector<HTMLElement>('.MuiBackdrop-root');
  if (backdrop) {
    backdrop.style.opacity = '0';
    backdrop.style.pointerEvents = 'none';
  }
}

function restoreMenu() {
  const root = document.querySelector<HTMLElement>('.MuiPopover-root');
  if (root) {
    root.style.zIndex = '';
    const backdrop = root.querySelector<HTMLElement>('.MuiBackdrop-root');
    if (backdrop) {
      backdrop.style.opacity = '';
      backdrop.style.pointerEvents = '';
    }
  }
}

export type AdminTourOptions = {
  isSuperAdmin: boolean;
  openRegistration?: () => void;
  closeRegistration?: () => void;
  openCheckin?: () => void;
  closeCheckin?: () => void;
};

export function buildAdminParticipantsTourSteps(
  t: TFunction,
  options: AdminTourOptions,
): DriveStep[] {
  const steps: DriveStep[] = [
    // 1. Welcome
    {
      popover: {
        title: t('participants.adminTour.steps.welcome.title'),
        description: t('participants.adminTour.steps.welcome.description'),
      },
    },
    // 2. Edition filter
    {
      element: sel('edition'),
      popover: {
        title: t('participants.adminTour.steps.edition.title'),
        description: t('participants.adminTour.steps.edition.description'),
      },
    },
    // 3. Filters — open the filter popover, highlight the panel
    {
      element: sel('filter-panel'),
      waitForElement: 3000,
      popover: {
        title: t('participants.adminTour.steps.filters.title'),
        description: t('participants.adminTour.steps.filters.description'),
      },
      onHighlightStarted: () => {
        clickTourElement('filter-btn');
      },
      onDeselected: () => {
        dismissPopover('filter-panel');
      },
    },
    // 4. Add participant
    {
      element: sel('add'),
      popover: {
        title: t('participants.adminTour.steps.add.title'),
        description: t('participants.adminTour.steps.add.description'),
      },
    },
    // 5. Participant list
    {
      element: sel('list'),
      popover: {
        title: t('participants.adminTour.steps.list.title'),
        description: t('participants.adminTour.steps.list.description'),
      },
    },
  ];

  // 6. Register — open the registration modal and highlight it
  if (options.openRegistration) {
    steps.push({
      element: '.MuiDialog-paper',
      waitForElement: 3000,
      popover: {
        title: t('participants.adminTour.steps.register.title'),
        description: t('participants.adminTour.steps.register.description'),
      },
      onHighlightStarted: () => {
        options.openRegistration?.();
        liftDialogForTour();
      },
      onDeselected: () => {
        restoreAndCloseDialog(options.closeRegistration);
      },
    });
  }

  // 7. Check-in — open the check-in modal and highlight it
  if (options.openCheckin) {
    steps.push({
      element: '.MuiDialog-paper',
      waitForElement: 3000,
      popover: {
        title: t('participants.adminTour.steps.checkin.title'),
        description: t('participants.adminTour.steps.checkin.description'),
      },
      onHighlightStarted: () => {
        options.closeRegistration?.();
        setTimeout(() => {
          options.openCheckin?.();
          liftDialogForTour();
        }, 300);
      },
      onDeselected: () => {
        restoreAndCloseDialog(options.closeCheckin);
      },
    });
  }

  // 8. QR scan
  steps.push({
    element: sel('scan-qr'),
    popover: {
      title: t('participants.adminTour.steps.scanQr.title'),
      description: t('participants.adminTour.steps.scanQr.description'),
    },
  });

  // 9. User check-in wizard
  steps.push({
    element: sel('wizard'),
    popover: {
      title: t('participants.adminTour.steps.wizard.title'),
      description: t('participants.adminTour.steps.wizard.description'),
    },
  });

  // 10. Edit participant — open three-dot menu, highlight Edit item
  steps.push({
    element: sel('edit-item'),
    waitForElement: 3000,
    popover: {
      title: t('participants.adminTour.steps.edit.title'),
      description: t('participants.adminTour.steps.edit.description'),
    },
    onHighlightStarted: () => {
      clickTourElement('menu');
      liftMenuForTour();
    },
    onDeselected: () => {
      restoreMenu();
      dismissMenu();
    },
  });

  // 11. Transfer — open three-dot menu, highlight Transfer item
  steps.push({
    element: sel('transfer-item'),
    waitForElement: 3000,
    popover: {
      title: t('participants.adminTour.steps.transfer.title'),
      description: t('participants.adminTour.steps.transfer.description'),
    },
    onHighlightStarted: () => {
      clickTourElement('menu');
      liftMenuForTour();
    },
    onDeselected: () => {
      restoreMenu();
      dismissMenu();
    },
  });

  // 12. Delete participant — open three-dot menu, highlight Delete item
  steps.push({
    element: sel('delete-item'),
    waitForElement: 3000,
    popover: {
      title: t('participants.adminTour.steps.delete.title'),
      description: t('participants.adminTour.steps.delete.description'),
    },
    onHighlightStarted: () => {
      clickTourElement('menu');
      liftMenuForTour();
    },
    onDeselected: () => {
      restoreMenu();
      dismissMenu();
    },
  });

  // 13. Delete all (superadmin only)
  if (options.isSuperAdmin) {
    steps.push({
      element: sel('delete-all'),
      popover: {
        title: t('participants.adminTour.steps.deleteAll.title'),
        description: t('participants.adminTour.steps.deleteAll.description'),
      },
    });
  }

  // 14. Export
  steps.push({
    element: sel('export'),
    popover: {
      title: t('participants.adminTour.steps.export.title'),
      description: t('participants.adminTour.steps.export.description'),
    },
  });

  // 15. Done
  steps.push({
    popover: {
      title: t('participants.adminTour.steps.done.title'),
      description: t('participants.adminTour.steps.done.description'),
    },
  });

  return steps;
}
